import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import type { InformeParametro as InformePersonalizadoParametro } from '../../services/informesStoreService';

interface Props {
  open: boolean;
  titulo: string;
  parametros: InformePersonalizadoParametro[];
  loading?: boolean;
  onClose: () => void;
  onConfirm: (values: Record<string, unknown>) => void;
}

const buildInitialValues = (parametros: InformePersonalizadoParametro[]): Record<string, unknown> => {
  const values: Record<string, unknown> = {};
  parametros.forEach((p) => {
    if (p.defaultValue !== undefined && p.defaultValue !== null) {
      values[p.name] = p.defaultValue;
      return;
    }
    switch ((p.type || 'string').toLowerCase()) {
      case 'boolean':
        values[p.name] = false;
        break;
      case 'number':
        values[p.name] = '';
        break;
      case 'date':
      case 'datetime':
        values[p.name] = '';
        break;
      default:
        values[p.name] = '';
    }
  });
  return values;
};

const InformePersonalizadoParametrosDialog: React.FC<Props> = ({
  open,
  titulo,
  parametros,
  loading = false,
  onClose,
  onConfirm,
}) => {
  const [values, setValues] = useState<Record<string, unknown>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (open) {
      setValues(buildInitialValues(parametros));
      setErrors({});
    }
  }, [open, parametros]);

  const sortedParams = useMemo(
    () => [...parametros].sort((a, b) => (a.promptText || a.name).localeCompare(b.promptText || b.name)),
    [parametros]
  );

  const handleConfirm = () => {
    const nextErrors: Record<string, string> = {};
    sortedParams.forEach((p) => {
      if (!p.required) return;
      const raw = values[p.name];
      const empty =
        raw === undefined ||
        raw === null ||
        (typeof raw === 'string' && raw.trim() === '');
      if (empty) nextErrors[p.name] = 'Obligatorio';
    });
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    const payload: Record<string, unknown> = {};
    sortedParams.forEach((p) => {
      const type = (p.type || 'string').toLowerCase();
      const raw = values[p.name];
      if (raw === '' || raw === undefined || raw === null) return;
      if (type === 'number') {
        const n = Number(raw);
        payload[p.name] = Number.isFinite(n) ? n : raw;
      } else if (type === 'boolean') {
        payload[p.name] = Boolean(raw);
      } else {
        payload[p.name] = raw;
      }
    });
    onConfirm(payload);
  };

  const setValue = (name: string, value: unknown) => {
    setValues((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Parámetros — {titulo}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Completá los parámetros requeridos antes de generar el informe.
        </Typography>
        <Stack spacing={2}>
          {sortedParams.map((p) => {
            const label = p.promptText || p.name;
            const type = (p.type || 'string').toLowerCase();
            const error = errors[p.name];
            const allowed = p.allowedValues?.filter(Boolean) ?? [];

            if (type === 'boolean') {
              return (
                <FormControlLabel
                  key={p.name}
                  control={
                    <Switch
                      checked={Boolean(values[p.name])}
                      onChange={(e) => setValue(p.name, e.target.checked)}
                      disabled={loading}
                    />
                  }
                  label={label}
                />
              );
            }

            if (allowed.length > 0) {
              return (
                <FormControl key={p.name} fullWidth error={Boolean(error)} disabled={loading}>
                  <InputLabel id={`${p.name}-label`}>{label}</InputLabel>
                  <Select
                    labelId={`${p.name}-label`}
                    label={label}
                    value={String(values[p.name] ?? '')}
                    onChange={(e) => setValue(p.name, e.target.value)}
                  >
                    {allowed.map((opt) => (
                      <MenuItem key={opt} value={opt}>
                        {opt}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              );
            }

            const inputType =
              type === 'number' ? 'number' : type === 'date' ? 'date' : type === 'datetime' ? 'datetime-local' : 'text';

            return (
              <TextField
                key={p.name}
                fullWidth
                label={label}
                type={inputType}
                value={String(values[p.name] ?? '')}
                onChange={(e) => setValue(p.name, e.target.value)}
                error={Boolean(error)}
                helperText={error}
                disabled={loading}
                InputLabelProps={inputType === 'date' || inputType === 'datetime-local' ? { shrink: true } : undefined}
              />
            );
          })}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancelar
        </Button>
        <Button variant="contained" onClick={handleConfirm} disabled={loading}>
          Generar informe
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InformePersonalizadoParametrosDialog;
